import { Fragment, useContext, useState } from "react";
import { GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { PolicyI } from "../../../../../../api/request/Lead/Interfaces/PolicyInterface";
import { BeneficiaryI } from "../../../../../../api/request/Lead/Interfaces/BeneficiaryInterface";
import { BeneficiaryDataI } from "../../../../../../api/request/Beneficiaries/Interfaces/BeneficiariesInterfaces";
import { Lead } from "../../../../../../api/request/Lead/Interfaces/DataInterface.response";
import numberFormat from "../../../../../../utils/Utils";
import { cardStyle, sectionTitleStyle, sectionTitleText } from "../cardStyles";
import PendingCapitalLine from "../pendingCapital/PendingCapitalLine";

interface PolicySummaryCardProps {
  lead?: Lead;
  policy?: PolicyI;
  beneficiaries?: BeneficiaryI[];
  resumeBeneficiaryIT?: BeneficiaryDataI[];
  beneficiaryIT: { type: string; nominative_beneficiary: number | null };
  domain?: string;
  navigateToRoute: (route: string) => void;
}

const PolicySummaryCard = ({
  lead,
  policy,
  beneficiaries,
  resumeBeneficiaryIT,
  beneficiaryIT,
  domain,
  navigateToRoute,
}: PolicySummaryCardProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);

  const [displayBeneficiaries, setDisplayBeneficiaries] = useState(false);

  const hasBeneficiaries =
    domain === "it" ? beneficiaryIT.type : lead?.beneficiaries;

  const beneficiariesLabel: Record<string, string> = {
    "Herederos Legales": translate("newDashboard.legalHeirs"),
    "Orden de Prelación": translate("newDashboard.prelationOrder"),
    other: translate("newDashboard.freeDesignation"),
    "Legal heirs": translate("newDashboard.legalHeirs"),
    "My children": translate("newDashboard.myChildren"),
    "Testament heirs": translate("newDashboard.testamentHeirs"),
    "My spouse": translate("newDashboard.mySpouse"),
    "Free designation": translate("newDashboard.freeDesignation"),
    Nominative: translate("newDashboard.nominative"),
  };

  const hasListedBeneficiaries =
    (beneficiaries && beneficiaries.length > 0) ||
    (resumeBeneficiaryIT && resumeBeneficiaryIT.length > 0);

  return (
    <div style={cardStyle}>
      <div style={sectionTitleStyle}>
        <GlobalIcon iconName="ShieldCheckIcon" size="XS" color="#4f46e5" />
        <h2 style={sectionTitleText}>
          {translate("newDashboard.redesign.policySummary")}
        </h2>
      </div>

      {/* Guarantees */}
      {policy?.traits && policy.traits.length > 0 && (
        <div style={{ paddingBottom: 16, borderBottom: "1px solid #f3f4f6", marginBottom: 16 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, margin: "0 0 10px" }}>
            {translate("newDashboard.redesign.guarantees")}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {policy.traits.map((t, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#f9fafb",
                  borderRadius: 8,
                  padding: "10px 14px",
                  border: "1px solid #f3f4f6",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <GlobalIcon iconName="ShieldCheckIcon" size="XS" color="#059669" />
                  <span style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>
                    {translate(`newDashboard.${t.trait}`)}
                  </span>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#059669" }}>
                    {numberFormat(t.capital, "capital")}
                  </span>
                  <PendingCapitalLine
                    pendingCapital={policy.pendingCapital}
                    current={t.capital}
                    trait={t.trait}
                    style={{ fontSize: 12, margin: "2px 0 0" }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Beneficiaries */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 13, fontWeight: 500, color: "#6b7280", margin: 0 }}>
              {translate("newDashboard.beneficiaries")}
            </p>
            {hasBeneficiaries && (
              <p style={{ fontSize: 15, fontWeight: 500, color: "#111827", margin: "4px 0 0" }}>
                {
                  beneficiariesLabel[
                    beneficiaryIT.nominative_beneficiary === 1
                      ? "Nominative"
                      : hasBeneficiaries
                  ]
                }
              </p>
            )}
          </div>
          {hasListedBeneficiaries && (
            <button
              onClick={() => setDisplayBeneficiaries(!displayBeneficiaries)}
              style={{ border: "none", background: "none", cursor: "pointer", padding: 6 }}
            >
              <span
                style={{
                  display: "block",
                  transition: "transform 0.2s",
                  transform: `rotate(${displayBeneficiaries ? 180 : 0}deg)`,
                }}
              >
                <GlobalIcon iconName="ChevronDown" size="XS" color="#6b7280" />
              </span>
            </button>
          )}
        </div>
        <div
          style={{
            overflow: "hidden",
            transition: "max-height 0.3s ease",
            maxHeight: displayBeneficiaries ? 600 : 0,
          }}
        >
          <div style={{ paddingTop: 12, paddingLeft: 16 }}>
            {beneficiaries?.map((beneficiary, idx) => {
              const { Nombre } = beneficiary;
              return (
                <Fragment key={`${Nombre}${idx}`}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#9ca3af", marginTop: 12, display: "block" }}>
                    {translate("newDashboard.menu.profile.name")}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 500, color: "#111827" }}>
                    {Nombre}
                  </span>
                </Fragment>
              );
            })}
            {resumeBeneficiaryIT?.map((beneficiary, idx) => {
              const { fullName, percentage, capital } = beneficiary;
              return (
                <Fragment key={`${fullName}${idx}`}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#9ca3af", marginTop: 12, display: "block" }}>
                    {translate("dashboard.policy.beneficiariesTitle")} {idx + 1}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 500, color: "#111827" }}>
                    {fullName}
                  </span>
                  <br />
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#4f46e5" }}>
                    {percentage}% = {numberFormat(capital, "capital")}
                  </span>
                </Fragment>
              );
            })}
          </div>
        </div>
        {!hasBeneficiaries && (!beneficiaries || beneficiaries.length === 0) && (
          <div
            style={{
              marginTop: 12,
              padding: 14,
              backgroundColor: "#fffbeb",
              borderRadius: 8,
              border: "1px solid #fef3c7",
            }}
          >
            <p style={{ fontSize: 14, color: "#92400e", margin: 0 }}>
              {translate("newDashboard.redesign.noBeneficiaries")}{" "}
              <button
                onClick={() => navigateToRoute("beneficiaries")}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  color: "#4f46e5",
                  fontWeight: 600,
                  fontSize: 14,
                  padding: 0,
                  textDecoration: "none",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
              >
                {translate("newDashboard.redesign.addBeneficiaries")}
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicySummaryCard;
